/**
 * Lead Notification Settings Component
 * Admin controls for automated lead notifications by country
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../ui/accordion';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { useToast } from '../../hooks/use-toast';
import { 
  Bell, Mail, Globe, Settings, Save, Trash2, Plus,
  Loader2, CheckCircle2, AlertTriangle, Clock, History,
  Banknote, Shield, X, Calendar, Play, Square, Timer
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'SL', name: 'Sri Lanka', flag: '🇱🇰', timezone: 'Asia/Colombo' },
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧', timezone: 'Europe/London' },
  { code: 'US', name: 'United States', flag: '🇺🇸', timezone: 'America/New_York' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', timezone: 'Australia/Sydney' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', timezone: 'America/Toronto' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪', timezone: 'Europe/Dublin' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', timezone: 'Pacific/Auckland' }
];

const TIMEZONES = [
  { value: 'Asia/Colombo', label: 'Asia/Colombo (Sri Lanka)' },
  { value: 'Europe/London', label: 'Europe/London (UK)' },
  { value: 'America/New_York', label: 'America/New York (US East)' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (US West)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney' },
  { value: 'America/Toronto', label: 'America/Toronto (Canada)' },
  { value: 'Europe/Dublin', label: 'Europe/Dublin (Ireland)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZ)' },
  { value: 'UTC', label: 'UTC' }
];

const DAYS_OF_WEEK = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' }
];

const FREQUENCIES = [
  { value: 'immediate', label: 'Immediate (every 5 min)' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' }
];

const LeadNotificationSettings = ({ className = '' }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [schedulerStatus, setSchedulerStatus] = useState({ running: false, jobs: [] });

  // Form state for editing
  const [formData, setFormData] = useState({
    country: '',
    enabled: true,
    finance_notifications: true,
    insurance_notifications: true,
    daily_threshold: 10,
    weekly_threshold: 50,
    notify_on_each_lead: false,
    admin_emails: [],
    notification_frequency: 'daily',
    schedule_enabled: false,
    schedule_time: '09:00',
    schedule_timezone: 'UTC',
    schedule_days: ['mon', 'tue', 'wed', 'thu', 'fri']
  });

  useEffect(() => {
    fetchSettings();
    fetchSchedulerStatus();
    fetchLogs();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/insurance/marketplace/notification-settings`);
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({ title: 'Error', description: 'Failed to load notification settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/insurance/marketplace/notification-logs?limit=20`);
      if (response.data.success) {
        setLogs(response.data.logs);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/insurance/marketplace/scheduler/status`);
      if (response.data.success) {
        setSchedulerStatus({
          running: response.data.scheduler_running,
          jobs: response.data.jobs || []
        });
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  const handleStartScheduler = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/insurance/marketplace/scheduler/initialize`);
      if (response.data.success) {
        toast({ title: 'Scheduler Started', description: response.data.message });
        await fetchSchedulerStatus();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to start scheduler', variant: 'destructive' });
    }
  };

  const handleStopScheduler = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/insurance/marketplace/scheduler/stop`);
      if (response.data.success) {
        toast({ title: 'Scheduler Stopped', description: response.data.message });
        await fetchSchedulerStatus();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to stop scheduler', variant: 'destructive' });
    }
  };

  const handleEditCountry = (countrySetting) => {
    const countryInfo = COUNTRIES.find(c => c.code === countrySetting.country);
    setEditingCountry(countrySetting.country);
    setFormData({
      country: countrySetting.country,
      enabled: countrySetting.enabled,
      finance_notifications: countrySetting.finance_notifications,
      insurance_notifications: countrySetting.insurance_notifications,
      daily_threshold: countrySetting.daily_threshold,
      weekly_threshold: countrySetting.weekly_threshold,
      notify_on_each_lead: countrySetting.notify_on_each_lead || false,
      admin_emails: countrySetting.admin_emails || [],
      notification_frequency: countrySetting.notification_frequency || 'daily',
      schedule_enabled: countrySetting.schedule_enabled || false,
      schedule_time: countrySetting.schedule_time || '09:00',
      schedule_timezone: countrySetting.schedule_timezone || countryInfo?.timezone || 'UTC',
      schedule_days: countrySetting.schedule_days || ['mon', 'tue', 'wed', 'thu', 'fri']
    });
  };

  const handleAddCountry = () => {
    // Find countries not yet configured
    const configuredCountries = settings.map(s => s.country);
    const availableCountry = COUNTRIES.find(c => !configuredCountries.includes(c.code));
    
    if (!availableCountry) {
      toast({ title: 'All countries configured', description: 'All available countries already have settings', variant: 'destructive' });
      return;
    }
    
    setFormData({
      country: availableCountry.code,
      enabled: true,
      finance_notifications: true,
      insurance_notifications: true,
      daily_threshold: 10,
      weekly_threshold: 50,
      notify_on_each_lead: false,
      admin_emails: [],
      notification_frequency: 'daily',
      schedule_enabled: false,
      schedule_time: '09:00',
      schedule_timezone: availableCountry.timezone || 'UTC',
      schedule_days: ['mon', 'tue', 'wed', 'thu', 'fri']
    });
    setShowAddDialog(true);
  };

  const handleToggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      schedule_days: prev.schedule_days.includes(day)
        ? prev.schedule_days.filter(d => d !== day)
        : [...prev.schedule_days, day]
    }));
  };

  const handleSave = async () => {
    if (formData.admin_emails.length === 0) {
      toast({ title: 'Warning', description: 'Add at least one admin email to receive notifications', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post(`${API_URL}/api/insurance/marketplace/notification-settings`, formData);
      
      if (response.data.success) {
        toast({ title: 'Saved', description: response.data.message });
        await fetchSettings();
        await fetchSchedulerStatus();
        setEditingCountry(null);
        setShowAddDialog(false);
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (country) => {
    if (!confirm(`Remove notification settings for ${country}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/insurance/marketplace/notification-settings/${country}`);
      toast({ title: 'Deleted', description: 'Notification settings removed' });
      await fetchSettings();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete settings', variant: 'destructive' });
    }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast({ title: 'Invalid email', variant: 'destructive' });
      return;
    }
    if (formData.admin_emails.includes(newEmail)) {
      toast({ title: 'Email already added', variant: 'destructive' });
      return;
    }
    setFormData(prev => ({
      ...prev,
      admin_emails: [...prev.admin_emails, newEmail]
    }));
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setFormData(prev => ({
      ...prev,
      admin_emails: prev.admin_emails.filter(e => e !== email)
    }));
  };

  const handleTestNotification = async (country) => {
    try {
      const response = await axios.post(`${API_URL}/api/insurance/marketplace/trigger-notification-check`, { country });
      toast({ 
        title: 'Test Complete', 
        description: response.data.message 
      });
      await fetchLogs();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to trigger test', variant: 'destructive' });
    }
  };

  const getCountryInfo = (code) => COUNTRIES.find(c => c.code === code) || { code, name: code, flag: '🌍' };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Bell className="h-5 w-5 text-purple-400" />
            Lead Notification Settings
          </h2>
          <p className="text-gray-400 text-sm mt-1">Configure automated email alerts when lead thresholds are reached</p>
        </div>
        <Button onClick={handleAddCountry} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="h-4 w-4 mr-2" />
          Add Country
        </Button>
      </div>

      {/* Scheduler Control Panel */}
      <Card className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border-purple-500/30 mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${schedulerStatus.running ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                {schedulerStatus.running ? (
                  <Play className="h-5 w-5 text-green-400" />
                ) : (
                  <Square className="h-5 w-5 text-red-400" />
                )}
              </div>
              <div>
                <p className="text-white font-medium">Notification Scheduler</p>
                <p className="text-gray-400 text-sm">
                  {schedulerStatus.running 
                    ? `Running with ${schedulerStatus.jobs.length} active job(s)` 
                    : 'Scheduler is stopped'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {schedulerStatus.running ? (
                <Button 
                  onClick={handleStopScheduler} 
                  variant="outline" 
                  size="sm"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Stop Scheduler
                </Button>
              ) : (
                <Button 
                  onClick={handleStartScheduler} 
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start Scheduler
                </Button>
              )}
            </div>
          </div>
          
          {/* Show scheduled jobs */}
          {schedulerStatus.running && schedulerStatus.jobs.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-gray-400 text-sm mb-2">Scheduled Jobs:</p>
              <div className="flex flex-wrap gap-2">
                {schedulerStatus.jobs.map((job, idx) => {
                  const countryInfo = getCountryInfo(job.country);
                  return (
                    <Badge key={idx} className="bg-purple-600/30 text-purple-200">
                      {countryInfo.flag} {countryInfo.name}
                      {job.next_run && (
                        <span className="ml-2 text-xs opacity-75">
                          Next: {new Date(job.next_run).toLocaleString()}
                        </span>
                      )}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Country Settings Cards */}
      <div className="grid gap-4 mb-8">
        {settings.map((countrySetting) => {
          const countryInfo = getCountryInfo(countrySetting.country);
          const isEditing = editingCountry === countrySetting.country;
          
          return (
            <Card key={countrySetting.country} className={`bg-white/5 border-white/10 ${isEditing ? 'ring-2 ring-purple-500' : ''}`}>
              <CardContent className="p-4">
                {isEditing ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <span className="text-2xl">{countryInfo.flag}</span>
                        {countryInfo.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingCountry(null)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <Separator className="bg-white/10" />

                    {/* Enable/Disable */}
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-300">Enable Notifications</Label>
                      <Switch 
                        checked={formData.enabled} 
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enabled: checked }))}
                      />
                    </div>

                    {/* Module Selection */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <span className="text-gray-300 flex items-center gap-2">
                          <Banknote className="h-4 w-4 text-emerald-400" />
                          Finance Leads
                        </span>
                        <Switch 
                          checked={formData.finance_notifications} 
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, finance_notifications: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <span className="text-gray-300 flex items-center gap-2">
                          <Shield className="h-4 w-4 text-indigo-400" />
                          Insurance Leads
                        </span>
                        <Switch 
                          checked={formData.insurance_notifications} 
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, insurance_notifications: checked }))}
                        />
                      </div>
                    </div>

                    {/* Thresholds */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300 mb-2 block">Daily Threshold</Label>
                        <div className="flex items-center gap-3">
                          <Slider
                            value={[formData.daily_threshold]}
                            onValueChange={([val]) => setFormData(prev => ({ ...prev, daily_threshold: val }))}
                            min={1}
                            max={100}
                            step={1}
                            className="flex-1"
                          />
                          <span className="text-white font-bold w-12 text-right">{formData.daily_threshold}</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-300 mb-2 block">Weekly Threshold</Label>
                        <div className="flex items-center gap-3">
                          <Slider
                            value={[formData.weekly_threshold]}
                            onValueChange={([val]) => setFormData(prev => ({ ...prev, weekly_threshold: val }))}
                            min={1}
                            max={500}
                            step={5}
                            className="flex-1"
                          />
                          <span className="text-white font-bold w-12 text-right">{formData.weekly_threshold}</span>
                        </div>
                      </div>
                    </div>

                    {/* Frequency */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">Notification Frequency</Label>
                      <Select 
                        value={formData.notification_frequency} 
                        onValueChange={(val) => setFormData(prev => ({ ...prev, notification_frequency: val }))}
                      >
                        <SelectTrigger className="bg-white/10 border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCIES.map(f => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">Minimum time between notifications</p>
                    </div>

                    {/* Admin Emails */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">Admin Email Recipients</Label>
                      <div className="flex gap-2 mb-2">
                        <Input
                          type="email"
                          placeholder="admin@example.com"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className="bg-white/10 border-white/20 text-white"
                          onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                        />
                        <Button onClick={handleAddEmail} variant="outline" className="border-white/20 text-white">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData.admin_emails.map((email, idx) => (
                          <Badge key={idx} className="bg-purple-600/30 text-purple-200 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {email}
                            <button onClick={() => handleRemoveEmail(email)} className="ml-1 hover:text-red-400">
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                        {formData.admin_emails.length === 0 && (
                          <span className="text-gray-500 text-sm">No recipients added</span>
                        )}
                      </div>
                    </div>

                    {/* Scheduled Reports Section */}
                    <div className="border border-white/10 rounded-lg p-4 bg-white/5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-5 w-5 text-purple-400" />
                          <Label className="text-white font-medium">Scheduled Daily Reports</Label>
                        </div>
                        <Switch 
                          checked={formData.schedule_enabled} 
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, schedule_enabled: checked }))}
                        />
                      </div>
                      
                      {formData.schedule_enabled && (
                        <div className="space-y-4 mt-4">
                          {/* Time Selection */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-gray-300 mb-2 block">Send Time</Label>
                              <Input
                                type="time"
                                value={formData.schedule_time}
                                onChange={(e) => setFormData(prev => ({ ...prev, schedule_time: e.target.value }))}
                                className="bg-white/10 border-white/20 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300 mb-2 block">Timezone</Label>
                              <Select 
                                value={formData.schedule_timezone} 
                                onValueChange={(val) => setFormData(prev => ({ ...prev, schedule_timezone: val }))}
                              >
                                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {TIMEZONES.map(tz => (
                                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Days Selection */}
                          <div>
                            <Label className="text-gray-300 mb-2 block">Send On Days</Label>
                            <div className="flex flex-wrap gap-2">
                              {DAYS_OF_WEEK.map(day => (
                                <button
                                  key={day.value}
                                  type="button"
                                  onClick={() => handleToggleDay(day.value)}
                                  className={`
                                    px-3 py-2 rounded-lg text-sm font-medium transition-all
                                    ${formData.schedule_days.includes(day.value)
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-white/10 text-gray-400 hover:bg-white/20'
                                    }
                                  `}
                                >
                                  {day.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <Alert className="bg-blue-500/10 border-blue-500/30">
                            <Timer className="h-4 w-4 text-blue-400" />
                            <AlertDescription className="text-blue-200 text-sm">
                              Reports will be sent at <strong>{formData.schedule_time}</strong> ({formData.schedule_timezone}) on selected days.
                              Make sure the scheduler is running.
                            </AlertDescription>
                          </Alert>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={() => setEditingCountry(null)} className="border-white/20 text-white">
                        Cancel
                      </Button>
                      <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Settings
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-3xl">{countryInfo.flag}</span>
                      <div>
                        <h3 className="font-semibold text-white">{countryInfo.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {countrySetting.notification_frequency}
                          </span>
                          <span>Daily: {countrySetting.daily_threshold}</span>
                          <span>Weekly: {countrySetting.weekly_threshold}</span>
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {countrySetting.admin_emails?.length || 0} recipients
                          </span>
                          {countrySetting.schedule_enabled && (
                            <span className="flex items-center gap-1 text-purple-400">
                              <Calendar className="h-3 w-3" />
                              {countrySetting.schedule_time}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={countrySetting.enabled ? 'bg-green-600/30 text-green-300' : 'bg-gray-600/30 text-gray-400'}>
                        {countrySetting.enabled ? 'Active' : 'Disabled'}
                      </Badge>
                      {countrySetting.schedule_enabled && (
                        <Badge className="bg-purple-600/30 text-purple-300">
                          <Calendar className="h-3 w-3 mr-1" />
                          Scheduled
                        </Badge>
                      )}
                      <div className="flex gap-1">
                        {countrySetting.finance_notifications && (
                          <Badge className="bg-emerald-600/30 text-emerald-300">
                            <Banknote className="h-3 w-3" />
                          </Badge>
                        )}
                        {countrySetting.insurance_notifications && (
                          <Badge className="bg-indigo-600/30 text-indigo-300">
                            <Shield className="h-3 w-3" />
                          </Badge>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleTestNotification(countrySetting.country)} className="text-gray-400 hover:text-white">
                        <Bell className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEditCountry(countrySetting)} className="text-gray-400 hover:text-white">
                        <Settings className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(countrySetting.country)} className="text-gray-400 hover:text-red-400">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {settings.length === 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8 text-center">
              <Bell className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No notification settings configured yet</p>
              <Button onClick={handleAddCountry} className="mt-4 bg-purple-600 hover:bg-purple-700">
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Country
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Notification History */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <History className="h-5 w-5 text-purple-400" />
            Recent Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length > 0 ? (
            <div className="space-y-2">
              {logs.map((log, idx) => {
                const countryInfo = getCountryInfo(log.country);
                return (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{countryInfo.flag}</span>
                      <div>
                        <p className="text-white text-sm">{log.notification_type?.replace('_', ' ')}</p>
                        <p className="text-gray-500 text-xs">
                          Sent to {log.recipients?.length || 0} recipients
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-400 text-sm">
                        {log.data?.total_today || 0} leads today
                      </p>
                      <p className="text-gray-500 text-xs">
                        {new Date(log.sent_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No notifications sent yet</p>
          )}
        </CardContent>
      </Card>

      {/* Add Country Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Add Country Settings</DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure lead notifications for a new country
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Select Country</Label>
              <Select 
                value={formData.country} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, country: val }))}
              >
                <SelectTrigger className="bg-white/10 border-white/20 text-white mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.filter(c => !settings.some(s => s.country === c.code)).map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.flag} {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Daily Threshold</Label>
                <Input
                  type="number"
                  value={formData.daily_threshold}
                  onChange={(e) => setFormData(prev => ({ ...prev, daily_threshold: parseInt(e.target.value) || 10 }))}
                  className="bg-white/10 border-white/20 text-white mt-2"
                />
              </div>
              <div>
                <Label className="text-gray-300">Weekly Threshold</Label>
                <Input
                  type="number"
                  value={formData.weekly_threshold}
                  onChange={(e) => setFormData(prev => ({ ...prev, weekly_threshold: parseInt(e.target.value) || 50 }))}
                  className="bg-white/10 border-white/20 text-white mt-2"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Admin Email</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="email"
                  placeholder="admin@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="bg-white/10 border-white/20 text-white"
                />
                <Button onClick={handleAddEmail} variant="outline" className="border-white/20 text-white">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.admin_emails.map((email, idx) => (
                  <Badge key={idx} className="bg-purple-600/30 text-purple-200">
                    {email}
                    <button onClick={() => handleRemoveEmail(email)} className="ml-1">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadNotificationSettings;
